import ThemeContext from '@aurora/shared-client/components/context/ThemeContext/ThemeContext';
import useCrossOriginAttribute from '@aurora/shared-client/components/useCrossOriginAttribute';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import type { OccasionTopicMessage } from '@aurora/shared-generated/types/graphql-schema-types';
import {
  BackgroundExtension,
  CoverImageStyle,
  CoverImageTitlePosition
} from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import UrlHelper from '@aurora/shared-utils/helpers/urls/UrlHelper/UrlHelper';
import * as React from 'react';
import { useContext } from 'react';
import { Image, useClassNameMapper } from 'react-bootstrap';
import { OccasionZoomMeetingVariant } from '../../../types/enums';
import type {
  BlogCoverImageFragment,
  ZoomMeetingDetailsQuery,
  ZoomMeetingDetailsQueryVariables
} from '../../../types/graphql-types';
import OccasionZoomMeeting from '../../occasions/OccasionZoomMeeting/OccasionZoomMeeting';
import zoomMeetingDetails from '../../occasions/OccasionZoomMeeting/ZoomMeetingDetails.query.graphql';
import useAutoRefresh from '../../useAutoRefresh';
import useTranslation from '../../useTranslation';
import localStyles from './MessageCoverImage.module.pcss';
import dynamic from 'next/dynamic';

const OccasionVideoPreview = dynamic(
  () => import('../../occasions/OccasionVideoPreview/OccasionVideoPreview'),
  { ssr: false }
);

interface Props {
  /**
   * Position of the cover image title.
   */
  titlePosition?: CoverImageTitlePosition;
  /**
   * Width variations in cover image.
   */
  imageStyle?: CoverImageStyle;
  /**
   * The alternative text for the cover image
   */
  altText?: string;
  /**
   * The Message to add cover image to.
   */
  message: OccasionTopicMessage | BlogCoverImageFragment;
  /**
   * Classname to be applied for the component.
   */
  className?: string;
  /**
   * Whether this is rendered from create/edit view
   */
  isCreateOrEditView?: boolean;
}

/**
 * Display Cover Image for a Message.
 * @author Arjun Krishna
 */
const MessageCoverImage: React.FC<React.PropsWithChildren<Props>> = ({
  message,
  titlePosition = CoverImageTitlePosition.Bottom,
  imageStyle = CoverImageStyle.Standard,
  altText = '',
  className,
  isCreateOrEditView = true
}) => {
  const cx = useClassNameMapper(localStyles);
  const { loading: textLoading } = useTranslation(EndUserComponent.MESSAGE_COVER_IMAGE);
  const href = message.coverImage?.url;
  const { theme } = useContext(ThemeContext);
  const { startTime, liveVideoIframe, endTime, liveStreamUrl } =
    (message as OccasionTopicMessage)?.occasionData || {};
  const isInProgress = useAutoRefresh(startTime, endTime, liveVideoIframe, 3600000);
  const isZoomOccasion = liveVideoIframe && liveVideoIframe?.match('zoom.us/j/');
  const crossOrigin = useCrossOriginAttribute();
  const { isAnonymous } = useRegistrationStatus();

  const queryResult = useQueryWithTracing<
    ZoomMeetingDetailsQuery,
    ZoomMeetingDetailsQueryVariables
  >(module, zoomMeetingDetails, {
    variables: {
      meetingUrl: liveVideoIframe
    },
    skip: !isZoomOccasion || !liveVideoIframe
  });

  const { data: zoomDetails, loading: zoomDetailsLoading } = queryResult;

  if (textLoading || (!liveVideoIframe && !href) || zoomDetailsLoading) {
    return null;
  }

  const isAnonWithZoomRegistrationRequired =
    !!zoomDetails?.zoomMeetingDetails?.result?.isRegistrationRequired && isAnonymous;

  const adjustedImageUrl =
    href &&
    UrlHelper.adjustImageServerPageUrl(
      href,
      {
        height: 2000,
        width: 2000
      },
      true,
      null,
      false
    );

  function renderLiveStreamVideo(): React.ReactElement {
    return isZoomOccasion ? (
      <OccasionZoomMeeting
        zoomUrl={liveVideoIframe}
        coverImageUrl={adjustedImageUrl}
        endTime={endTime}
        variant={OccasionZoomMeetingVariant.LARGE}
      />
    ) : (
      <OccasionVideoPreview />
    );
  }

  function renderBannerImage(): React.ReactElement {
    return (
      <Image
        src={adjustedImageUrl}
        alt={altText}
        className={cx('lia-cover-image')}
        data-testid="MessageCoverImage-Image"
        crossOrigin={crossOrigin(adjustedImageUrl)}
      />
    );
  }

  if (!adjustedImageUrl && (!liveStreamUrl || isAnonWithZoomRegistrationRequired)) {
    return null;
  }

  const hasCoverImage = !!message?.coverImage;
  const hasCoverVideo = liveVideoIframe && isInProgress;
  const hasCoverZoomMeeting = hasCoverVideo && isZoomOccasion;
  const finalImageStyle = imageStyle ?? CoverImageStyle.Standard;
  const useWidthOfPageContent =
    theme?.basics?.pageWidthStyle === BackgroundExtension.WidthOfPageContent &&
    finalImageStyle === CoverImageStyle.Wide;

  return (
    <div
      className={cx(
        'lia-cover-image-wrap',
        { 'lia-possition-top': titlePosition === CoverImageTitlePosition.Top },
        { 'lia-possition-overlay': titlePosition === CoverImageTitlePosition.Overlay },
        { 'lia-width-wide': finalImageStyle === CoverImageStyle.Wide && hasCoverImage },
        { 'lia-width-standard': finalImageStyle === CoverImageStyle.Standard && hasCoverImage },
        { 'lia-is-page-content-width': useWidthOfPageContent },
        { 'lia-has-cover-video': hasCoverVideo },
        { 'lia-is-zoom-meeting': hasCoverZoomMeeting },
        className
      )}
    >
      {isAnonWithZoomRegistrationRequired
        ? renderBannerImage()
        : !!liveVideoIframe && isInProgress && !isCreateOrEditView
        ? renderLiveStreamVideo()
        : renderBannerImage()}
    </div>
  );
};

export default MessageCoverImage;
